import PIECard, { PIECardModes } from '@src/shared/components/PIECard';
import React from 'react';
import style from './DashboardOrderCard.module.scss';
import PIEText from '@src/shared/components/PIEText';
import { IconBike, IconUser, IconLocationPinFilled } from '@justeattakeaway/pie-icons-react';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { globalStyles } from 'snacks-design-system';
import TagAlcohol from '../DashboardCustomTag/components/TagAlcohol';
import TagUnpaid from '../DashboardCustomTag/components/TagUnpaid';
import TagDelivered from '../DashboardCustomTag/components/TagDelivered';
import { OrderStatus } from '../../models/model-enum';
import TagAssigned from '../DashboardCustomTag/components/TagAssigned';
import TagInDelivery from '../DashboardCustomTag/components/TagInDelivery';
import DashboardOrderTimeVisualizer from '../DashboardOrderTimeVisualizer';
import TagDelayed from '../DashboardCustomTag/components/TagDelayed';
import TagDue from '../DashboardCustomTag/components/TagDue';
import dayjs from 'dayjs';
import useDashboardStore from '../../hooks/use-dashboard-store';
import useBackIn from '@src/pages/Deliveries/hooks/use-back-in';
import TagUnassigned from '../DashboardCustomTag/components/TagUnassigned';
import TagPreOrder from '../DashboardCustomTag/components/TagPreOrder';
import { IOrder, IRoute, IWaypoint } from '../../models/model-interface';

interface IDashboardOrderCard {
  dueTime?: string | Boolean;
  orderETA: string | Boolean;
  address: string;
  city: string;
  postCode: string;
  isPaid: Boolean;
  isContainAlcohol: Boolean;
  customerName: string | Boolean;
  orderStatus?: OrderStatus;
  onClick?: () => void;
  elevation?: Boolean;
  isOrderDetail?: Boolean;
  courier?: IOrder['courier'] | undefined;
  updateDate: string | undefined;
  uuid: string;
  isCourierDetailView?: boolean;
  isOrderDetailView?: boolean;
  isPreOrder?: boolean;
}

const { xdsSpacingA } = globalStyles;

const DashboardOrderCard = ({
  dueTime,
  orderETA,
  orderStatus,
  isContainAlcohol,
  updateDate,
  isPaid,
  address,
  city,
  postCode,
  customerName,
  onClick,
  elevation = true,
  isOrderDetail,
  courier,
  uuid,
  isCourierDetailView = false,
  isOrderDetailView = false,
  isPreOrder = false,
}: IDashboardOrderCard) => {
  const { getMinutesFromThat } = useBackIn();
  const { serverTime, courierRoute, courierNextRoute } = useDashboardStore();

  const getCourierWaypoints = (courierId: string | undefined): IWaypoint[] => {
    if (!courierId) {
      return [];
    }

    const currentWaypoints =
      courierRoute?.find((r: IRoute) => r.courier.id === courierId)?.waypoints || [];
    const nextWaypoints =
      courierNextRoute?.find((r: IRoute) => r.courier.id === courierId)?.waypoints || [];

    const allWaypoints = [...currentWaypoints, ...nextWaypoints];

    return allWaypoints.length > 0 ? allWaypoints : [];
  };

  const getOrderWaypointIndex = (): number | undefined => {
    const waypoints = getCourierWaypoints(courier?.id);
    if (!waypoints) {
      return undefined;
    }

    const waypointIndex = waypoints.findIndex(
      (waypoint: IWaypoint) => waypoint.order.uuid === uuid
    );

    const indexDelivered = waypoints.filter(
      (waypoint: IWaypoint) => waypoint.isDelivered === true
    ).length;
    return waypointIndex !== -1 ? waypointIndex + (indexDelivered + 1) : 0;
  };

  const getStatusTag = (status: OrderStatus) => {
    const Tags = {
      [OrderStatus.Delivered]: TagDelivered,
      [OrderStatus.Assigned]: TagAssigned,
      [OrderStatus.InDelivery]: TagInDelivery,
      [OrderStatus.AssignedToNextPool]: TagAssigned,
      [OrderStatus.Unassigned]: TagUnassigned,
    };

    const ShownTag = Tags[status];

    if (!ShownTag) {
      return null;
    }

    return (
      <div className={style.DashboardOrderCardInformationStatusTag}>
        <ShownTag />
      </div>
    );
  };

  const getOrderTimeTags = (data: 'Delayed' | 'Due', showTimeValue = true) => {
    const calculateDelayedTime = showTimeValue
      ? getMinutesFromThat(dueTime, serverTime)
      : undefined;
    switch (data) {
      case 'Delayed':
        return <TagDelayed delayedTime={calculateDelayedTime} />;
      case 'Due':
        return <TagDue dueTime={calculateDelayedTime} />;
      default:
        return null;
    }
  };

  const renderWaypointInfo = () => {
    return getOrderWaypointIndex() && getCourierWaypoints(courier?.id).length > 1 ? (
      <>
        <PIEText
          text={
            <>
              <IconLocationPinFilled size="xs" className={style.DashboardOrderCardCourierIcon} />

              {getOrderWaypointIndex()}
            </>
          }
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-bold)"
          truncate={true}
        />

        <PIEText
          text={' | '}
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-regular)"
          color="var(--xds-color-border-default)"
          truncate={true}
        />
      </>
    ) : (
      <></>
    );
  };
  const getTagCombination = (orderStatus: OrderStatus) => {
    switch (orderStatus) {
      case OrderStatus.Assigned:
      case OrderStatus.InDelivery:
        return (
          <div
            className={` ${
              getCourierWaypoints(courier?.id).length < 2 &&
              isCourierDetailView &&
              orderStatus === OrderStatus.InDelivery &&
              !dayjs(dueTime as any).isBefore(serverTime?.heartbeat as any)
                ? style.noBorder
                : style.DashboardOrderCardOrderTag
            }`}
          >
            {courier ? (
              <>
                {isCourierDetailView && orderStatus === OrderStatus.InDelivery
                  ? renderWaypointInfo()
                  : null}

                <PIEText
                  text={
                    !isCourierDetailView ? (
                      <>
                        <IconBike size="xs" className={style.DashboardOrderCardCourierIcon} />
                        {courier.lastName}
                      </>
                    ) : orderStatus === OrderStatus.Assigned ? (
                      <TagAssigned />
                    ) : null
                  }
                  size="var(--xds-font-size-14)"
                  fontWeight="var(--xds-font-weight-bold)"
                  truncate={true}
                />
              </>
            ) : null}
            {isOrderDetailView ? (
              orderStatus !== OrderStatus.InDelivery ? (
                <TagAssigned />
              ) : (
                <TagInDelivery />
              )
            ) : null}
            {!isOrderDetail && isPreOrder ? <TagPreOrder /> : null}
            {dayjs(dueTime as any).isBefore(serverTime?.heartbeat as any)
              ? getOrderTimeTags('Delayed', false)
              : null}
          </div>
        );

      case OrderStatus.Unassigned:
        if (isOrderDetail) {
          return (
            <div className={style.DashboardOrderCardOrderTag}>
              {renderWaypointInfo()}
              {isOrderDetail ? getStatusTag(orderStatus) : null}
              {dayjs(dueTime as any).isBefore(serverTime?.heartbeat as any)
                ? getOrderTimeTags('Delayed')
                : dayjs(dueTime as any).diff(serverTime?.heartbeat, 'm') <= 15
                ? getOrderTimeTags('Due')
                : null}
            </div>
          );
        }
        if (dayjs(dueTime as any).isBefore(serverTime?.heartbeat as any) && !isOrderDetail) {
          return (
            <div className={style.DashboardOrderCardOrderTag}>
              {!isOrderDetail && isPreOrder ? <TagPreOrder /> : null}
              {getOrderTimeTags('Delayed')}
            </div>
          );
        }
        const isDue = dayjs(dueTime as any).diff(serverTime?.heartbeat, 'm') <= 15;
        if (isPreOrder || isDue) {
          return (
            <div className={style.DashboardOrderCardOrderTag}>
              {!isOrderDetail && isPreOrder ? <TagPreOrder /> : null}
              {isDue ? getOrderTimeTags('Due') : null}
            </div>
          );
        }
        return null;

      case OrderStatus.Delivered:
        return (
          <div className={style.DashboardOrderCardOrderTag}>
            {courier ? (
              <PIEText
                text={
                  isOrderDetailView ? (
                    <>
                      <IconBike size="xs" className={style.DashboardOrderCardCourierIcon} />
                      {courier.lastName}
                    </>
                  ) : null
                }
                size="var(--xds-font-size-14)"
                fontWeight="var(--xds-font-weight-bold)"
                truncate={true}
              />
            ) : null}
            {!isOrderDetail && isPreOrder ? <TagPreOrder /> : null}
            <TagDelivered deliveredTime={dayjs(updateDate).format('HH:mm')} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <PIECard
      className={style.DashboardOrderCardWrapper}
      mode={PIECardModes.REGULAR}
      onClick={onClick}
      modElevation={elevation}
    >
      <div className={style.DashboardOrderCardWrapperMs2}>
        <div className={style.DashboardOrderCardOrderInformationContainer}>
          <div className={style.DashboardOrderCardOrderInformation}>
            <PIEText
              text={address}
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
              color="var(--xds-color-content-subdued)"
              truncate={true}
            />
            <PIEText
              text={`${postCode} , ${city}`}
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
              truncate={true}
            />
            <div>
              <PIEText
                size="var(--xds-font-size-16)"
                fontWeight="var(--xds-font-weight-regular)"
                color="var(--xds-color-content-subdued)"
                truncate={true}
                text={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <IconUser
                      size="xs"
                      className={style.DashboardOrderCardInformationCourierNameIcon}
                    />
                    {customerName}
                  </div>
                }
              ></PIEText>
            </div>
          </div>
          <div className={style.DashboardOrderCardInformationSubInfo}>
            {isContainAlcohol && (
              <PIEComponentUtil marginRight={xdsSpacingA}>
                <TagAlcohol />
              </PIEComponentUtil>
            )}
            {!isPaid && (
              <PIEComponentUtil marginRight={xdsSpacingA}>
                <TagUnpaid />
              </PIEComponentUtil>
            )}
          </div>
        </div>
        <div className={style.DashboardOrderCardOrderInformation}>
          {serverTime && (
            <DashboardOrderTimeVisualizer
              compareTime={serverTime as any}
              dueTime={dueTime}
              orderStatus={orderETA ? (orderStatus as any) : undefined}
              showInDeliveryDueTime={isOrderDetail || false}
              etaTime={orderETA}
            />
          )}
        </div>
      </div>
      {getTagCombination(orderStatus as OrderStatus)}
    </PIECard>
  );
};

export default DashboardOrderCard;
